import React, { useState } from "react";
import { Button, Form, Modal, Row, Col, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import { CreateExternalFileRequestDto } from "../../../../interfaces/ExternalFileReqeustDtos/externalFileRequestDtos";
import { CreateExternalFileRequestAsync } from "../../../../services/ExternalFileRequestService/ExternalFileRequestService";
import { useAuth } from "../../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import { SpaceIcon, Trash2 } from "lucide-react";

interface ExternalFileRequestModalProps {
  showExternalFileReqeustModal: boolean;
  handleCloseExternalFileReqeustModal: () => void;
  taskId: number;
  //ak
  onSuccess?: () => void;
}

interface FormState {
  email: string;
  description: string;
  expireDays: number | null;
  requestedFiles: string[];
}

const ExternalFileRequestModal: React.FC<ExternalFileRequestModalProps> = ({
  handleCloseExternalFileReqeustModal,
  showExternalFileReqeustModal,
  taskId,
  //ak
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [validated, setValidated] = useState<boolean>(false);
  const [formState, setFormState] = useState<FormState>({
    email: "",
    description: "",
    expireDays: null,
    requestedFiles: [""],
  });

  const [loading, setLoading] = useState<boolean>(false);

  const handleRequestSend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setValidated(true);
    const payload: CreateExternalFileRequestDto = {
      taskId: taskId,
      requestedDisplayNames: formState.requestedFiles
        .map((f) => f?.trim()) // safely trim even if null
        .filter((f) => f && f.length > 0), // remove empty or null entries
      recipientEmail: formState.email?.trim() || "",
      description: formState.description?.trim() || null,
      expirationDays: formState.expireDays ?? null,
    };

    const token = await currentUser?.getIdToken();
    if (!token) return;

    setLoading(true);
    CreateExternalFileRequestAsync(token, payload)
      .then((res) => {
        if (res.success) {
          toast.success(res.message);
          handleCloseExternalFileReqeustModal();
          //ak
          onSuccess?.();
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Error to create Request");
      })
      .finally(() => setLoading(false));
  };

  const handleAddFile = () => {
    setFormState((prev) => {
      if (prev.requestedFiles.length >= 10) {
        toast.warn("You can request up to 10 files only.");
        return prev;
      }
      return { ...prev, requestedFiles: [...prev.requestedFiles, ""] };
    });
  };

  const handleRemoveFile = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      requestedFiles: prev.requestedFiles.filter((_, i) => i !== index),
    }));
  };

  const handleFileNameChange = (index: number, value: string) => {
    setFormState((prev) => {
      const newFiles = [...prev.requestedFiles];
      newFiles[index] = value;
      return { ...prev, requestedFiles: newFiles };
    });
  };

  return (
    <Modal
      show={showExternalFileReqeustModal}
      onHide={handleCloseExternalFileReqeustModal}
      centered
      backdrop="static" // prevent outside click close
      keyboard={false} // prevent Esc close
    >
      <Form noValidate validated={validated} onSubmit={handleRequestSend}>
        <div className="modal-activity-reject-header">
          <h2 className="modal-activity-reject-title">External File Request</h2>
          <button
            className="modal-activity-reject-close"
            onClick={handleCloseExternalFileReqeustModal}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <Modal.Body
          style={{
            overflowY: "hidden",
          }}
        >
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Email</Form.Label>
              <Form.Control
                required
                type="email"
                placeholder="Enter recipient email"
                value={formState.email}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, email: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                Please enter a valid email address.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description (optional)"
                value={formState.description}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiration Days (optional)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter Expiration Days (optional)"
                min={1}
                max={30}
                value={formState.expireDays ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormState((prev) => ({
                    ...prev,
                    expireDays: v === "" ? null : Number(v),
                  }));
                }}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a valid number of days (1–30).
              </Form.Control.Feedback>
            </Form.Group>

            <hr />

            <Form.Label> Requested File Names</Form.Label>

            {formState.requestedFiles.map((file, index) => (
              <div
                className="mb-3 flex items-center gap-4"
                key={index}
                style={{
                  alignItems: "center !important",
                }}
              >
                <Form.Control
                  required
                  className=""
                  type="text"
                  placeholder={`Requested file name #${index + 1}`}
                  value={file}
                  onChange={(e) => handleFileNameChange(index, e.target.value)}
                />

                <span
                  onClick={() => {
                    formState.requestedFiles.length > 1 &&
                      handleRemoveFile(index);
                  }}
                >
                  <Trash2
                    stroke="#FB2C36"
                    size={16}
                    className="cursor-pointer"
                  />
                </span>
              </div>
            ))}

            <span
              className="mb-3"
              style={{
                color: "#717182",
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: "1rem",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "1.25rem",

                padding: "0.3rem 0.407rem 0.45rem 0.625rem",
                marginBottom: "1rem",
                cursor: "pointer",
              }}
              onClick={handleAddFile}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Another
              File
            </span>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "none", padding: "1.5rem 2rem" }}>
          <Button
            className="btn btn-secondary cancel-btn"
            onClick={handleCloseExternalFileReqeustModal}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary submit-btn"
            type="submit"
            disabled={loading}
          >
            {loading ? "Sending Request..." : "Send Request"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ExternalFileRequestModal;
